import { FC, useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { User } from "src/api/types";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import moment from "moment";

type Props = {
  user: User;
};

type EventItem = {
  id: number;
  action: string;
  createdUtc: string;
};

const UserEventsTab: FC<Props> = ({ user }) => {
  const [dataSource, setDataSource] = useState<EventItem[]>([]);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  /* ---------------- GET EVENTS ---------------- */

  const { mutate: getUserKycEvents, isLoading } = useMutation(
    client.newUserKycInfo.getKycInfoEvents,
    {
      onSuccess: (data) => {
        setDataSource(data?.items || []);
        if (data?.message === "You do not have access for this route!") {
          setAlertText(data?.message);
          setIsAlertOpened(true);
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    },
  );

  useEffect(() => {
    if (user?.id) getUserKycEvents({ id: user.id });
  }, [user?.id]);

  /* ---------------- FORMAT ACTION ---------------- */

  const formatAction = (action: string) => {
    if (!action) return "";

    return action
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  /* ---------------- UI ---------------- */

  if (isLoading) return <div className="flex justify-center items-center h-40 text-gray-400">Loading events...</div>;

  return (
    <div className="w-full px-6">
      <h2 className="text-lg font-semibold mb-6">User Session Log</h2>

      {dataSource.length === 0 && (
        <p className="text-gray-500">No events found</p>
      )}

      {/* Timeline */}
      <ul className="relative">
        {/* vertical line */}
        <div
          className="absolute left-4 top-0 bottom-0 w-[2px]"
          style={{ border: "1px dashed #bdbdbd" }}
        />

        {dataSource.map((event) => (
          <li key={event.id} className="relative flex items-center py-5">
            {/* timeline column */}
            <div className="relative w-8 flex justify-center">
              {/* dot centered on line */}
              <span
                className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-gray-500 z-10"
                style={{ background: "#787878" }}
              />
            </div>

            {/* content */}
            <div className="ml-4 flex justify-between w-full">
              <h6 className="font-manrope font-semibold text-[14px] text-primary">
                {formatAction(event.action)}
              </h6>

              <p className="text-[14px] text-gray-500 whitespace-nowrap">
                {moment(event.createdUtc).format("YYYY-MM-DD HH:mm")}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default UserEventsTab;
